import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { APP_NAME_BUREAU, APP_NAME_UMBRELLA } from '@nxsys/store/constants/keys';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  validateLeaverIssuesApi,
  submitLeaverIssuesApi,
} from '@nxsys/services/api/employee';
import { AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { showMessage } from '@nxsys/utils/message';
import { Checkbox, Tag } from 'antd';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubmitIssueP45Modal, {
  SubmitIssueP45Values,
} from '../../reports/management/components/SubmitIssueP45Modal';

type LeaverIrelandModalProps = {
  isOpen: boolean;
  toggle: () => void;
};

type ValidateResult = {
  subcontractorId: string;
  balance?: number;
  message?: string;
  isExistTsP45?: boolean;
  isProcessing?: boolean;
};

type ValidateResponse = {
  results: ValidateResult[];
  lastestLeaveDate?: string;
  lastestPaymentDate?: string;
};

const LeaverIrelandModal: React.FC<LeaverIrelandModalProps> = ({
  isOpen,
  toggle,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { user, isBureau } = useSelector((state: AppState) => state.appStore);

  const [employees, setEmployees] = useState<EmployeeRecordType[]>([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [checkBoxHeader, setCheckBoxHeader] = useState(false);

  const [validateData, setValidateData] = useState<ValidateResponse | null>(
    null,
  );
  const [isOpenSubmitModal, setIsOpenSubmitModal] = useState(false);
  const [p45DefaultDate, setP45DefaultDate] = useState<Moment | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // NX-5228: this used to hard-code `appName: APP_NAME_BUREAU` regardless
      // of the real tenant. The backend derives `isBureau` from `appName`
      // when no explicit `isBureau` is sent (subcontractor.v2.service.ts),
      // so a hard-coded BUREAU appName silently applied the bureau payment-
      // model allow-list to an umbrella caller — an umbrella-Ireland (or any
      // umbrella) tenant's UMBRELLA-model workers would never be listed here.
      const res = await (dispatch as any)(
        employeeActions.getEmployeeList({
          page,
          size,
          status: ['ACTIVE'],
          appName: isBureau ? APP_NAME_BUREAU : APP_NAME_UMBRELLA,
          companyIds: user?.companyUserIds || [],
        }),
      ).unwrap();
      setEmployees(res?.data ?? []);
      setTotal(res?.pagination?.totalDocs ?? 0);
    } catch {
      // handled by store
    } finally {
      setLoading(false);
    }
  }, [page, size, dispatch, user, isBureau]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelectedRowIds([]);
      setCheckBoxHeader(false);
      setValidateData(null);
    }
  }, [isOpen, page, size]);

  const handleSelectAll = (checked: boolean) => {
    setCheckBoxHeader(checked);
    setSelectedRowIds(checked ? employees.map((e) => e.id) : []);
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedRowIds((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id),
    );
  };

  const getRowValidation = (id: string): ValidateResult | undefined =>
    validateData?.results?.find((r) => r.subcontractorId === id);

  const getRowClassName = (record: EmployeeRecordType) => {
    const v = getRowValidation(record.id);
    if (!v) return '';
    if (v.message && !v.message.toLowerCase().includes('holiday pay'))
      return 'bg-red-50';
    if (v.balance || v.message) return 'bg-yellow-50';
    return '';
  };

  const handleValidate = async () => {
    if (!selectedRowIds.length) {
      showMessage({
        type: 'warning',
        content: messages['common.pleaseSelectEmployee'] ?? 'Please select employees',
      });
      return;
    }
    try {
      setLoading(true);
      const res = await validateLeaverIssuesApi({ ids: selectedRowIds });
      setValidateData(res);
      if (res.lastestPaymentDate) {
        setP45DefaultDate(moment(res.lastestPaymentDate));
      }

      const hasValid = res?.results?.some(
        (r: ValidateResult) => !r.message || r.isExistTsP45,
      );
      if (hasValid) {
        setIsOpenSubmitModal(true);
      } else {
        showMessage({
          type: 'warning',
          content: 'No valid employees to submit',
        });
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: SubmitIssueP45Values) => {
    const validIds = selectedRowIds.filter((id) => {
      const v = getRowValidation(id);
      return !v?.message || v?.isExistTsP45;
    });

    if (!validIds.length) return;

    try {
      setLoading(true);
      await submitLeaverIssuesApi(
        { ids: validIds },
        {
          dateLeft: moment(values.dateLeft).toISOString(),
          reason: values.reason?.value ?? '',
        },
      );
      showMessage({
        type: 'success',
        content: messages['common.markLeaverSuccess'] ?? 'Leaver submitted successfully',
      });
      setSelectedRowIds([]);
      setCheckBoxHeader(false);
      setValidateData(null);
      setIsOpenSubmitModal(false);
      fetchData();
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={checkBoxHeader}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'id',
      width: 50,
      render: (id: string) => (
        <Checkbox
          checked={selectedRowIds.includes(id)}
          onChange={(e) => handleSelectRow(id, e.target.checked)}
        />
      ),
    },
    {
      title: messages['common.systemId'] ?? 'Employee ID',
      dataIndex: 'id',
      width: 120,
    },
    {
      title: messages['common.name'],
      dataIndex: 'fullName',
      width: 200,
    },
    {
      title: messages['common.email'],
      dataIndex: 'email',
      width: 250,
    },
    {
      title: messages['common.mobile'],
      dataIndex: 'mobile',
      width: 150,
    },
    {
      title: messages['common.ppsNumber'] ?? 'PPS Number',
      dataIndex: ['setting', 'ppsNumber'],
      width: 150,
    },
    {
      title: messages['common.message'] ?? 'Message',
      dataIndex: 'id',
      width: 400,
      render: (id: string) => {
        const v = getRowValidation(id);
        if (!v?.message) return null;
        return (
          <Tag color={v.isExistTsP45 ? 'orange' : 'red'}>{v.message}</Tag>
        );
      },
    },
  ];

  return (
    <>
      <AppModalContainer
        title={messages['common.leaver'] ?? 'Leaver'}
        isOpen={isOpen}
        toggle={toggle}
        width="80vw"
        actions={[
          {
            name: messages['common.cancel'],
            variant: 'outlined',
            onClick: toggle,
          },
          {
            name: messages['common.submit'] ?? 'Submit',
            buttonType: 'primary',
            onClick: handleValidate,
            disabled: !selectedRowIds.length,
            loading,
          },
        ]}
      >
        <AppTableContainer
          columns={columns}
          data={employees}
          loading={loading}
          rowKey="id"
          rowClassName={getRowClassName}
          scroll={{ x: 1300 }}
          page={page}
          pageSize={size}
          total={total}
          onChangePagination={(p: number, s: number) => {
            if (s !== size) {
              setSize(s);
              setPage(0);
            } else {
              setPage(p - 1);
            }
          }}
        />
      </AppModalContainer>

      <SubmitIssueP45Modal
        isOpen={isOpenSubmitModal}
        toggle={() => setIsOpenSubmitModal(false)}
        onSubmit={handleSubmit}
        title={messages['common.leaver'] ?? 'Leaver'}
        defaultDate={p45DefaultDate}
      />
    </>
  );
};

export default LeaverIrelandModal;
